"use client";
import React from "react";

import { Box, BoxSx, Text } from "../../foundations";
import { DropdownOption } from "../TextField/DropdownMenu/types";

export interface DropdownMenuProps {
  dropdownOptions: DropdownOption[];
  showNoResults?: boolean;
  noResultsMessage?: string;
  isOpen: boolean;
  selectedOption?: DropdownOption;
  onOptionSelect: (option: DropdownOption) => void;
  onDismiss: () => void;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  dropdownOptions,
  showNoResults = false,
  noResultsMessage = "No results found",
  isOpen,
  selectedOption,
  onOptionSelect,
  onDismiss
}) => {
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);

  const optionRefs = React.useRef<HTMLElement[]>([]);
  optionRefs.current = [];

  const menuRef = React.useRef<HTMLDivElement>(null);

  // focus updated on open
  React.useEffect(() => {
    if (isOpen && highlightedIndex >= 0) {
      const option = optionRefs.current[highlightedIndex] as HTMLElement;
      option?.focus();
    }
  }, [highlightedIndex, isOpen]);

  // focus updated when using keyboard nav
  React.useEffect(() => {
    if (isOpen) {
      const idx = dropdownOptions.findIndex(
        (opt) => opt.value === selectedOption?.value
      );
      setHighlightedIndex(idx >= 0 ? idx : -1);
      menuRef.current?.focus();
    }
  }, [isOpen, dropdownOptions, selectedOption]);

  if (!isOpen) {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < dropdownOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : dropdownOptions.length - 1
        );
        break;
      case "Enter":
        event.preventDefault();
        const dropdownOptionHighlighted = dropdownOptions[highlightedIndex];
        if (highlightedIndex !== -1 && dropdownOptionHighlighted) {
          onOptionSelect(dropdownOptionHighlighted);
        }
        break;
      case "Tab":
      case "Escape":
        event.preventDefault();
        onDismiss();
        break;
      default:
        break;
    }
  };

  const setOptionRef = (el: HTMLElement | null, index: number) => {
    if (el) {
      optionRefs.current[index] = el;
    }
  };

  const dropdownContainerStyles: BoxSx = {
    position: "absolute",
    top: "100%",
    left: 0,
    width: "100%",
    maxHeight: () => "250px",
    overflowY: "auto",
    border: "1px solid",
    borderColor: "input-stroke-enabled",
    borderRadius: "radius-2",
    backgroundColor: "input-onSurface",
    boxShadow: "shadow-100",
    zIndex: "dropdown",
    transition: "transition-color-medium"
  };

  const dropdownItemContainerStyles: BoxSx = {
    px: "spacing-5",
    py: "spacing-3",
    transition: "transition-color-medium",
    ":hover": {
      backgroundColor: "surface-brand"
    }
  };

  const dropdownItemStyles: BoxSx = {
    borderBottomWidth: 0,
    borderColor: "transparent",
    borderInlineEndWidth: 0,
    borderInlineStartWidth: "3px",
    borderStyle: "solid",
    borderTopWidth: 0,
    cursor: "pointer",
    px: "spacing-4",
    py: "spacing-2",
    transition: "transition-color-medium"
  };

  return (
    <Box
      sx={dropdownContainerStyles}
      role="listbox"
      ref={menuRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {dropdownOptions.length > 0 ? (
        dropdownOptions.map((option, index) => {
          const isHighlighted = index === highlightedIndex;
          const isSelected = selectedOption?.value === option.value;

          return (
            <Box
              key={option.value}
              role="option"
              tabIndex={-1}
              aria-selected={isHighlighted}
              ref={(el) => setOptionRef(el, index)}
              onClick={() => onOptionSelect(option)}
              onMouseEnter={() => setHighlightedIndex(index)}
              sx={dropdownItemContainerStyles}
            >
              <Box
                sx={{
                  ...dropdownItemStyles,
                  ...(isSelected && {
                    borderColor: "onSurface-brand-primary"
                  })
                }}
              >
                <Text variant="body-1-regular" sx={{ color: "currentColor" }}>
                  {option.label}
                </Text>
              </Box>
            </Box>
          );
        })
      ) : showNoResults ? (
        <Box
          role="option"
          sx={{ padding: "spacing-4", color: "text-incidental" }}
        >
          {noResultsMessage}
        </Box>
      ) : null}
    </Box>
  );
};
